import { agentShortName } from "./naming.js";
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
/**
 * The general agent (stored role null) is displayed and addressed as "general" everywhere,
 * so no other agent may claim that word as its role: two agents answering to "@owner/general"
 * is an ambiguity people cannot resolve by typing.
 */
export const RESERVED_AGENT_ROLE = "general";
/** Whether a requested role would collide with the name the role-null general agent already uses. */
export function isReservedAgentRole(role) {
    return role.trim().toLowerCase() === RESERVED_AGENT_ROLE;
}
export function normalizeAddress(raw) {
    return raw.trim().toLowerCase().replace(/^@/, "");
}
/**
 * Every name an agent answers to under one owner: its stored handle ("alice-qa-bot"), its role
 * ("reviewer") and the short name the dashboard prints (agentShortName, "general" for the
 * role-null agent). Keeping these in one place is what makes anything agentAddress() prints
 * — "@alice/qa-bot", "alice/qa-bot", "qa-bot" — typeable back as an address.
 */
function matchesName(agent, ownerHandle, token) {
    if (agent.handle.toLowerCase() === token)
        return true;
    if (agent.role !== null && agent.role.toLowerCase() === token)
        return true;
    return agentShortName(agent, ownerHandle).toLowerCase() === token;
}
export function resolveAddress(raw, context) {
    const token = normalizeAddress(raw);
    if (!token)
        return { ok: false, reason: "not_found", candidates: [] };
    if (UUID_PATTERN.test(token)) {
        // A raw or @-prefixed agent UUID addresses exactly one agent; retired agents are not in allAgents.
        const direct = context.allAgents.find((agent) => agent.id.toLowerCase() === token);
        return direct ? pick([direct]) : { ok: false, reason: "not_found", candidates: [] };
    }
    if (token.includes("/")) {
        const [owner, name, ...rest] = token.split("/");
        if (!owner || !name || rest.length > 0) {
            return { ok: false, reason: "not_found", candidates: [] };
        }
        const ownerHandle = (owner === "me" ? context.senderUserHandle : owner).toLowerCase();
        return pick(context.allAgents.filter((agent) => agent.ownerHandle.toLowerCase() === ownerHandle && matchesName(agent, ownerHandle, name)));
    }
    // A bare name means the sender's own agent first; only then a teammate's.
    const ownMatches = context.senderAgents.filter((agent) => matchesName(agent, context.senderUserHandle, token));
    if (ownMatches.length > 0)
        return pick(ownMatches);
    // A bare user handle means that person's general agent.
    const usernameMatches = context.allAgents.filter((agent) => agent.ownerHandle.toLowerCase() === token && agent.role === null);
    if (usernameMatches.length > 0)
        return pick(usernameMatches);
    return pick(context.allAgents.filter((agent) => matchesName(agent, agent.ownerHandle, token)));
}
function pick(matches) {
    // One agent can match twice: a retired agent is offered through its fallback, so the same
    // id appears under both names. Collapse those before counting.
    const unique = [...new Map(matches.map((agent) => [agent.id, agent])).values()]
        .map((agent) => ({ id: agent.id, handle: agent.handle, role: agent.role }));
    // "general" is the general agent's name, so it wins over an agent whose role is that word.
    const generals = unique.filter((agent) => agent.role === null);
    const candidates = unique.length > 1 && generals.length > 0 && generals.length < unique.length
        ? generals
        : unique;
    if (candidates.length === 1) {
        return { ok: true, agent: candidates[0] };
    }
    return {
        ok: false,
        reason: candidates.length === 0 ? "not_found" : "ambiguous",
        candidates,
    };
}
//# sourceMappingURL=address.js.map